import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Modal, Row, Col, Button } from 'react-bootstrap';
import exitIntent from 'exit-intent-quattro';

import cookie from '../../../../util/cookie.js';
import storage from '../../../../util/storage.js';

import { getCartItems } from '../../../Cart/CartActions.js';
import { getLoggedUser } from '../../../User/UserActions.js';
import { getProducts, getProductSpecialPriceDateThreshold } from '../../../Product/ProductActions.js';
import { isPopinActive, setPopinActive } from '../../AppActions.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import Icon from '../../../../components/Content/Icon.js';
import { getUserGroup, USER_GROUP_DEFAULT } from '../../../User/UserGroupActions.js';
import ReviewSummary from '../../../Review/components/view/ReviewSummary.js';
import Chrono from '../../../../components/Time/Chrono.js';
import StarRating from '../../../Review/components/view/StarRating.js';

const SESSION_KEY = 'exitIntentDeadline';

function toNativeDate(d) {
    if(!d) return null;
    if(d instanceof Date) return d;
    if(typeof d.toDate === 'function') return d.toDate(); // dayjs object
    if(typeof d === 'string') return new Date(d);
    return null;
}

function resolveDeadline(products) {
    const now = Date.now();
    const futures = (products || [])
        .flatMap(p => (p.specialPrices || []))
        .map(sp => toNativeDate(getProductSpecialPriceDateThreshold(sp)))
        .filter(d => d instanceof Date && !Number.isNaN(d.getTime()) && d.getTime() > now)
        .sort((a, b) => a - b);

    if(futures.length > 0) return futures[0];

    try {
        const stored = storage.getItem(SESSION_KEY);
        if(stored) {
            const d = new Date(stored);
            if(d.getTime() > now) return d;
        }
    } catch (e) { /* sessionStorage may be blocked */ }
    const deadline = new Date(now + 60 * 60 * 1000);
    try {
		storage.setItem(SESSION_KEY, deadline.toISOString());
	} catch (e) { /* sessionStorage may be blocked */ }
    return deadline;
}

function ExitIntent({ dispatch, intl, user = null, userGroup = null, cartItems = [], products = [], popinActive = false }) {
    const [isDisplayed, setIsDisplayed] = useState(false);
    const [modalAlreadyDisplayed, setModalAlreadyDisplayed] = useState(false);
    const [deadline, setDeadline] = useState(null);

    useEffect(() => {
        if(cookie.getItem('exitIntent')) {
            setModalAlreadyDisplayed(true);
        } else {
            exitIntent({
                edges: { top: true, right: false, bottom: false, left: false },
                threshold: 5,
                maxDisplays: 5,
                eventThrottle: 100,
                onExitIntent: () => {
                    if(!popinActive && !modalAlreadyDisplayed) {
                        setIsDisplayed(true);
                    }
                },
            });
        }
    }, []);

    useEffect(() => {
        if(isDisplayed && !deadline) {
            setDeadline(resolveDeadline(products));
        }
    }, [isDisplayed]);

    const toggle = (event, display = null) => {
        const displayModal = display !== null ? display : !isDisplayed;
        setIsDisplayed(displayModal);
        dispatch(setPopinActive(displayModal));
        if(!displayModal) {
            setModalAlreadyDisplayed(true);
            process.env.NODE_ENV === 'production' && cookie.setItem('exitIntent', 1, { maxAge: 24 * 60 * 60 });
        }
    };

    const getNextStepLink = () => (cartItems.length ? `/${intl.locale}/cart` : '#');
    const hasCart = cartItems.length > 0;

    if(
        !modalAlreadyDisplayed
        && (!user || process.env.NODE_ENV === 'development')
        && userGroup?.identifier === USER_GROUP_DEFAULT
    ) {
        return (
            <Modal size="lg" show={isDisplayed} onHide={toggle} centered contentClassName="overflow-hidden p-0">

                <div className="bg-danger-gradient text-white p-4 text-center">
                    <small className="d-block text-uppercase opacity-75 mb-1" style={{ letterSpacing: '0.1em', fontSize: '0.7rem' }}>
                        Vous partez déjà ?
                    </small>
                    <p className="fs-4 fw-semibold mb-0">
                        Ne partez pas sans vos cadeaux !
                    </p>
                    {deadline && (
                        <div className="d-inline-flex align-items-center gap-1 mt-3 rounded-1 px-2 py-1 small bg-white-gradient text-danger">
                            <Icon icon="time" />&nbsp;Offres réservées encore&nbsp;<strong className="font-monospace"><Chrono date={deadline} hideOnEnd onEnd={() => setDeadline(null)} /></strong>
                        </div>
                    )}
                </div>

                <div className="bg-info bg-texture p-3 pb-2">
                    <Row className="g-2 mb-3">
                        <Col xs="12" md="5">
                            <div className="bg-white border rounded p-3 h-100 text-center shadow">
                                <span className="badge bg-secondary mb-2">
                                    dès 30€ d&apos;achat
                                </span>
                                <p className="fw-semibold mb-1">🥩 Saucisson surprise</p>
                                <p className="text-danger fw-bold mb-2">OFFERT</p>
                                <small className="text-muted">Code <strong>SURPRISE</strong> à saisir dans le panier</small>
                            </div>
                        </Col>
                        <Col xs="12" md="2" className="d-flex align-items-center justify-content-center py-2 py-md-0">
                            <span className="text-secondary fw-bold">OU</span>
                        </Col>
                        <Col xs="12" md="5">
                            <div className="bg-white border rounded p-3 h-100 text-center shadow">
                                <span className="badge bg-secondary mb-2">
                                    dès 90€ d&apos;achat
                                </span>
                                <p className="fw-semibold mb-1">🍖 Noix de jambon</p>
                                <p className="text-danger fw-bold mb-2">OFFERTE</p>
                                <small className="text-muted">Nature ou poivre (400-500g), ajoutée automatiquement</small>
                            </div>
                        </Col>
                    </Row>

                    <div className="rounded p-3 text-center">
                        <strong>🚚 Livraison GRATUITE</strong> en point-relais — France métropolitaine &amp; Belgique
                        <br />
                        <small className="opacity-75">dès 20€ d&apos;achat, réservée aux particuliers</small>
                    </div>

                    <p className="text-muted text-center mt-2 mb-0">
                        <small>Offres valables une fois par client, non cumulables, réservées aux particuliers.</small>
                    </p>
                </div>

                <div className="p-3 text-center">
                    <Button
                        variant="outline-danger"
                        size="lg"
                        className="w-100 mb-2 fw-semibold"
                        as={Link}
                        to={getNextStepLink()}
                        onClick={toggle}
                    >
                        {hasCart ? 'Finaliser ma commande et récupérer mes cadeaux 🛒' : '🛍️ Voir les produits et profiter des offres'}
                    </Button>

                    <StarRating centered />

                    <Button
                        size="sm"
                        variant="link"
                        className="w-100 text-muted mt-1"
                        onClick={toggle}
                    >
                        Non merci, je pars sans mes cadeaux
                    </Button>
                </div>

            </Modal>
        );
    }

    if(process.env.NODE_ENV === 'development') {
        return <Button onClick={toggle}>Display exit intent popin {modalAlreadyDisplayed && 'already displayed'} {userGroup?.identifier !== USER_GROUP_DEFAULT && 'group not allowed'}</Button>;
    }

    return null;
}

function mapStateToProps(store) {
    return {
        user: getLoggedUser(store),
        userGroup: getUserGroup(store),
        cartItems: getCartItems(store),
        products: getProducts(store),
        popinActive: isPopinActive(store),
    };
}

ExitIntent.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    user: PropTypes.object,
    userGroup: PropTypes.object,
    cartItems: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.arrayOf(PropTypes.shape({})),
    popinActive: PropTypes.bool,
};

ExitIntent.defaultProps = {
    products: [],
};

export default connect(mapStateToProps)(withRouter(injectIntl(ExitIntent)));
